/*
  Generic component used to display notifications coming from
  notifications API endpoint. It's also used by notification-items.js
  to display those generic components in top right corner of the app
  if user has notifications turned on.
 */
import { mapActions } from 'vuex';
import socialLevelMixin from '@/mixins/socialLevel';
import dashboardNotificationMixin from '@/mixins/dashboardNotification';
import marketChangePrice from '@/components/market/market-change-price/market-change-price.vue';
import { isMarketSingle, toCents } from '@/utils/helpers';
import { NOTIFICATION_MATCHED, NOTIFICATION_SETTLED, NOTIFICATION_PRICE_CHANGE, NOTIFICATION_LEVEL } from '@/services/notifications/proxy';

const NOTIFICATION_CLASS_SETTLED = 'settled';
const NOTIFICATION_CLASS_PRICE_UP = 'price-up';
const NOTIFICATION_CLASS_PRICE_DOWN = 'price-down';
const NOTIFICATION_CLASS_MATCHED = 'matched';

export default {
  mixins: [
    dashboardNotificationMixin,
    socialLevelMixin,
  ],
  props: {
    showTimeElapsed: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    marketChangePrice,
  },
  computed: {
    iconTargetClass() {
      switch (this.data.notificationType) {
        case NOTIFICATION_MATCHED:
          return NOTIFICATION_CLASS_MATCHED;
        case NOTIFICATION_SETTLED:
          return NOTIFICATION_CLASS_SETTLED;
        case NOTIFICATION_PRICE_CHANGE:
          return (this.data.contentJson.PriceDelta > 0) ?
            NOTIFICATION_CLASS_PRICE_UP :
            NOTIFICATION_CLASS_PRICE_DOWN;
        default:
          return NOTIFICATION_CLASS_SETTLED;
      }
    },
    classModifiers() {
      const isUnread = this.theme !== 'notifications'
        && this.data && typeof this.data.isRead !== 'undefined' && !this.data.isRead;
      return {
        'notification-item-dashboard': true,
        'notification-item-dashboard--unread': isUnread,
        [`notification-item-dashboard--${this.theme}`]: this.theme,
        'notification-item-dashboard--clickable': this.marketId,
      };
    },
    marketId() {
      return this.data.contentJson ? this.data.contentJson.MarketId : null;
    },
    iconClassModifiers() {
      return {
        'notification-item-dashboard__icon': true,
        // [`notification-item-dashboard__icon--${this.iconTargetClass}`]: this.iconTargetClass,
      };
    },
    iconStyleModifiers() {
      return {
        'background-image': `url(${this.data.contentJson.ImageUrl})`,
      };
    },
    showContractName() {
      return this.data.notificationType === NOTIFICATION_PRICE_CHANGE;
    },
    showPayout() {
      return this.data.notificationType === NOTIFICATION_SETTLED;
    },
    showSellInfo() {
      return this.data.notificationType === NOTIFICATION_MATCHED;
    },
    showSharesOwned() {
      return this.data.notificationType === NOTIFICATION_PRICE_CHANGE;
    },
    showPriceChange() {
      return this.data.notificationType === NOTIFICATION_PRICE_CHANGE;
    },
    showLevelChanged() {
      return this.data.notificationType === NOTIFICATION_LEVEL;
    },
    showNextLevelInfo() {
      return this.data.contentJson.NewTraderLevel < 7;
    },
    quantity() {
      return this.data.contentJson.Quantity;
    },
    sharesOwned() {
      return this.data.contentJson.SharesOwned;
    },
    quantityModifiers() {
      const tradeType = this.data.contentJson.TradeType;
      return {
        'notification-item-dashboard__quantity': true,
        'notification-item-dashboard__quantity--negative': (tradeType === 0 || tradeType === 2),
      };
    },
    pricePerShare() {
      return toCents(this.data.contentJson.PricePerShare);
    },
    isMarketSingle() {
      return isMarketSingle(this.data.contentJson);
    },
    tradeTypeShare() {
      return (typeof this.data.contentJson.MarketType === 'undefined' || this.isMarketSingle) ?
        '' :
        (this.data.contentJson.ContractName || '');
    },
    tradeTypeOperation() {
      return this.data.contentJson.TradeType <= 1 ? 'Bought' : 'Sold';
    },
    lastTradePriceAsCents() {
      return toCents(this.data.contentJson.LastTradePrice);
    },
    routeTo() {
      if (this.marketId) {
        return {
          name: 'market-details',
          params: {
            marketId: this.marketId,
          },
        };
      }
      return {};
    },
  },
  methods: {
    ...mapActions('notifications', ['closeNotificationsWidget']),
    onInteract() {
      this.closeNotificationsWidget();
    },
  },
};



// WEBPACK FOOTER //
// ./src/components/notification-items/notifcation-item-dashboard/notification-item-dashboard.js